import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  ScrollView,
  StyleSheet,
  Dimensions,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../contexts/ThemeContext';
import Tooltip from '../components/Tooltip';
import Footer from '../components/Footer';
import { formatCurrency, formatNumber } from '../utils/formatters';

const { width } = Dimensions.get('window');

const GSTCalculatorScreen = ({ navigation }) => {
  const { colors, isDarkMode } = useTheme();
  
  // State variables
  const [userType, setUserType] = useState('buyer');
  const [amount, setAmount] = useState('100');
  const [profitRatio, setProfitRatio] = useState(10);
  const [gstRate, setGstRate] = useState('18');
  const [isGstIncluded, setIsGstIncluded] = useState('no');
  const [results, setResults] = useState(null);
  const [showFAQ, setShowFAQ] = useState(true);
  const [showGstDropdown, setShowGstDropdown] = useState(false);
  
  // Slider-related hooks
  const sliderRef = React.useRef(null);
  const [isDragging, setIsDragging] = React.useState(false);
  
  // Screen dimensions for responsive design
  const [screenData, setScreenData] = useState(Dimensions.get('window'));
  const isWeb = Platform.OS === 'web';
  const isMobile = screenData.width < 900;

  // Update screen dimensions on resize
  useEffect(() => {
    const subscription = Dimensions.addEventListener('change', ({ window }) => {
      setScreenData(window);
    });
    return () => subscription?.remove();
  }, []);

  const gstRates = [
    { value: '0', label: '0%' },
    { value: '0.25', label: '0.25%' },
    { value: '3', label: '3%' },
    { value: '5', label: '5%' },
    { value: '12', label: '12%' },
    { value: '18', label: '18%' },
    { value: '28', label: '28%' },
  ];

  const userTypes = [
    { value: 'buyer', label: 'Buyer' },
    { value: 'manufacturer', label: 'Manufacturer' },
    { value: 'wholesaler', label: 'Wholesaler' },
  ];

  // Calculate GST in real-time
  useEffect(() => {
    if (amount && !isNaN(parseFloat(amount)) && parseFloat(amount) > 0) {
      calculateGST();
    } else {
      setResults(null);
    }
  }, [amount, gstRate, isGstIncluded, profitRatio, userType]);

  const calculateGST = () => {
    if (!amount || isNaN(amount) || parseFloat(amount) <= 0) {
      setResults(null);
      return;
    }

    const inputAmount = parseFloat(amount);
    const rate = parseFloat(gstRate);
    
    let costPrice, salePrice, gstAmount, totalAmount;

    if (userType === 'buyer') {
      // For buyer, amount is the sale price
      if (isGstIncluded === 'yes') {
        // GST is included in the sale price
        totalAmount = inputAmount;
        salePrice = inputAmount / (1 + rate / 100);
        gstAmount = inputAmount - salePrice;
        costPrice = salePrice;
      } else {
        // GST is not included in the sale price
        salePrice = inputAmount;
        gstAmount = (inputAmount * rate) / 100;
        totalAmount = inputAmount + gstAmount;
        costPrice = salePrice;
      }
    } else {
      // For manufacturer/wholesaler, amount is the cost price
      costPrice = inputAmount;
      salePrice = costPrice * (1 + profitRatio / 100);
      
      if (isGstIncluded === 'yes') {
        // GST is included in the sale price
        totalAmount = salePrice;
        salePrice = salePrice / (1 + rate / 100);
        gstAmount = totalAmount - salePrice;
      } else {
        // GST is not included in the sale price
        gstAmount = (salePrice * rate) / 100;
        totalAmount = salePrice + gstAmount;
      }
    }

    // Calculate CGST and SGST (assuming intra-state transaction)
    const cgst = gstAmount / 2;
    const sgst = gstAmount / 2;

    setResults({
      costPrice: Math.round(costPrice * 100) / 100,
      salePrice: Math.round(salePrice * 100) / 100,
      gstAmount: Math.round(gstAmount * 100) / 100,
      totalAmount: Math.round(totalAmount * 100) / 100,
      cgst: Math.round(cgst * 100) / 100,
      sgst: Math.round(sgst * 100) / 100,
      gstRate: rate,
      profitRatio: userType !== 'buyer' ? profitRatio : 0,
      userType
    });
  };

  const renderHeader = () => (
    <View style={styles.header}>
      {isMobile && (
        <TouchableOpacity 
          style={styles.hamburgerButton}
          onPress={() => navigation.openDrawer?.()}
        >
          <Ionicons name="menu" size={24} color="white" />
        </TouchableOpacity>
      )}
      <Text style={styles.headerTitle}>GST Calculator for FY 2024-25 & 2025-26</Text>
      <Text style={styles.headerSubtitle}>
        Calculate your GST liability with our advanced calculator. Compare exclusive vs inclusive calculations, plan your business transactions, and optimize your tax compliance. Get accurate results for both CGST and SGST and find the best approach.
      </Text>
      
      <View style={styles.headerTags}>
        <View style={styles.headerTag}>
          <Text style={styles.headerTagText}>All GST Rates</Text>
        </View>
        <View style={styles.headerTag}>
          <Text style={styles.headerTagText}>Latest Slabs</Text>
        </View>
        <View style={styles.headerTag}>
          <Text style={styles.headerTagText}>CGST & SGST</Text>
        </View>
        <View style={styles.headerTag}>
          <Text style={styles.headerTagText}>100% Free</Text>
        </View>
      </View>
    </View>
  );

  const renderUserTypeSelector = () => (
    <View style={styles.inputGroup}>
      <View style={styles.userTypeContainer}>
        {userTypes.map((type) => (
          <TouchableOpacity
            key={type.value}
            style={[
              styles.userTypeButton,
              userType === type.value && styles.userTypeButtonSelected
            ]}
            onPress={() => setUserType(type.value)}
          >
            <View style={[
              styles.radioButton,
              userType === type.value && styles.radioButtonSelected
            ]}>
              {userType === type.value && <View style={styles.radioButtonInner} />}
            </View>
            <Text style={styles.userTypeText}>{type.label}</Text>
          </TouchableOpacity>
        ))}
      </View>
    </View>
  );

  const getAmountLabel = () => {
    switch (userType) {
      case 'buyer':
        return 'Sale Price';
      case 'manufacturer':
        return 'Cost of Production';
      case 'wholesaler':
        return 'Cost of goods';
      default:
        return 'Amount';
    }
  };

  const renderAmountInput = () => (
    <View style={styles.inputGroup}>
      <Text style={styles.label}>{getAmountLabel()}</Text>
      <View style={styles.inputContainer}>
        <Text style={styles.currencySymbol}>₹</Text>
        <TextInput
          style={styles.input}
          value={amount}
          onChangeText={setAmount}
          placeholder="100"
          placeholderTextColor={colors.textSecondary}
          keyboardType="numeric"
        />
      </View>
    </View>
  );

  const renderProfitRatioSlider = () => {
    if (userType === 'buyer') return null;
    
    const updateSliderValue = (event) => {
      if (sliderRef.current) {
        sliderRef.current.measure((x, y, width, height, pageX, pageY) => {
          const { locationX } = event.nativeEvent;
          const percentage = Math.max(0, Math.min(100, (locationX / width) * 100));
          setProfitRatio(Math.round(percentage));
        });
      }
    };

    const handleResponderGrant = (event) => {
      setIsDragging(true);
      updateSliderValue(event);
    };

    const handleResponderRelease = () => {
      setIsDragging(false);
    };

    return (
      <View style={styles.inputGroup}>
        <Text style={styles.label}>Profit Ratio</Text>
        <View style={styles.sliderContainer}>
          <View 
            ref={sliderRef}
            style={[styles.sliderTrack, isDragging && styles.sliderTrackActive]}
            onStartShouldSetResponder={() => true}
            onMoveShouldSetResponder={() => true}
            onResponderGrant={handleResponderGrant}
            onResponderMove={updateSliderValue}
            onResponderRelease={handleResponderRelease}
            onResponderTerminate={handleResponderRelease}
          >
            <View 
              style={[
                styles.sliderFill, 
                { width: `${profitRatio}%` }
              ]} 
            />
            <View 
              style={[
                styles.sliderThumb, 
                { left: `${profitRatio}%` },
                isDragging && styles.sliderThumbActive
              ]} 
            />
          </View>
          <View style={styles.sliderLabels}>
            <Text style={styles.sliderValue}>{profitRatio}</Text>
            <Text style={styles.sliderUnit}>%</Text>
          </View>
        </View>
      </View>
    );
  };

  const renderGSTRateDropdown = () => (
    <View style={styles.inputGroup}>
      <Text style={styles.label}>GST Rate(%)</Text>
      <TouchableOpacity 
        style={styles.dropdownContainer}
        onPress={() => setShowGstDropdown(!showGstDropdown)}
      >
        <Text style={styles.dropdownValue}>{gstRate}%</Text>
        <Ionicons 
          name={showGstDropdown ? "chevron-up" : "chevron-down"} 
          size={20} 
          color={colors.textSecondary} 
        />
      </TouchableOpacity>
      {showGstDropdown && (
        <View style={styles.gstRateOptions}>
          {gstRates.map((rate) => (
            <TouchableOpacity
              key={rate.value}
              style={[
                styles.gstRateOption,
                gstRate === rate.value && styles.gstRateOptionSelected
              ]}
              onPress={() => {
                setGstRate(rate.value);
                setShowGstDropdown(false);
              }}
            >
              <Text style={[
                styles.gstRateText,
                gstRate === rate.value && styles.gstRateTextSelected
              ]}>
                {rate.label}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      )}
    </View>
  );

  const renderGSTInclusionDropdown = () => (
    <View style={styles.inputGroup}>
      <Text style={styles.label}>Is GST included in sale price?</Text>
      <TouchableOpacity 
        style={styles.dropdownContainer}
        onPress={() => setIsGstIncluded(isGstIncluded === 'yes' ? 'no' : 'yes')}
      >
        <Text style={styles.dropdownValue}>
          {isGstIncluded === 'yes' ? 'Yes' : 'No'}
        </Text>
        <Ionicons name="chevron-down" size={20} color={colors.textSecondary} />
      </TouchableOpacity>
    </View>
  );

  const renderDonutChart = () => {
    if (!results) return null;

    const { salePrice, gstAmount } = results;
    const total = salePrice + gstAmount;
    const salePricePercentage = (salePrice / total) * 100;
    const gstPercentage = (gstAmount / total) * 100;

    return (
      <View style={styles.chartContainer}>
        <View style={styles.donutChart}>
          <View style={styles.donutBase}>
            {/* Sale Price segment */}
            <View style={[styles.donutSegment, {
              borderTopColor: salePricePercentage > 25 ? '#4CAF50' : 'transparent',
              borderRightColor: salePricePercentage > 50 ? '#4CAF50' : 'transparent',
              borderBottomColor: salePricePercentage > 75 ? '#4CAF50' : 'transparent',
              borderLeftColor: '#4CAF50',
              transform: [{ rotate: '0deg' }],
            }]} />
            {/* GST segment */}
            <View style={[styles.donutSegment, {
              borderTopColor: gstPercentage > 25 ? '#FF6B6B' : 'transparent',
              borderRightColor: gstPercentage > 50 ? '#FF6B6B' : 'transparent',
              borderBottomColor: gstPercentage > 75 ? '#FF6B6B' : 'transparent',
              borderLeftColor: gstPercentage > 0 ? '#FF6B6B' : 'transparent',
              transform: [{ rotate: `${(salePricePercentage / 100) * 360}deg` }],
            }]} />
          </View>
        </View>
        <View style={styles.chartLegend}>
          <View style={styles.legendItem}>
            <View style={[styles.legendColor, { backgroundColor: '#4CAF50' }]} />
            <Text style={styles.legendText}>Sale Price</Text>
          </View>
          <View style={styles.legendItem}>
            <View style={[styles.legendColor, { backgroundColor: '#FF6B6B' }]} />
            <Text style={styles.legendText}>Total GST</Text>
          </View>
        </View>
      </View>
    );
  };

  const renderResults = () => {
    if (!results) {
      return (
        <View style={styles.resultsContainer}>
          <View style={styles.emptyResults}>
            <Ionicons name="calculator-outline" size={48} color={colors.textSecondary} />
            <Text style={styles.emptyResultsText}>Enter an amount to see GST calculation</Text>
          </View>
        </View>
      );
    }

    return (
      <View style={styles.resultsContainer}>
        {renderDonutChart()}
        
        <View style={styles.resultsList}>
          <View style={styles.resultRow}>
            <Text style={styles.resultLabel}>Sale Price</Text>
            <Text style={styles.resultValue}>₹{results?.salePrice?.toLocaleString() || '0'}</Text>
          </View>
          
          <View style={styles.resultRow}>
            <Text style={styles.resultLabel}>CGST</Text>
            <Text style={styles.resultValue}>₹{results?.cgst?.toLocaleString() || '0'}</Text>
          </View>
          
          <View style={styles.resultRow}>
            <Text style={styles.resultLabel}>SGST</Text>
            <Text style={styles.resultValue}>₹{results?.sgst?.toLocaleString() || '0'}</Text>
          </View>
          
          <View style={[styles.resultRow, styles.totalRow]}>
            <Text style={styles.totalLabel}>Total GST</Text>
            <Text style={styles.totalValue}>₹{results?.gstAmount?.toLocaleString() || '0'}</Text>
          </View>
        </View>
      </View>
    );
  };

  const renderGSTInformation = () => (
    <View style={styles.infoContainer}>
      <Text style={styles.infoTitle}>GST Calculator - Complete Guide for FY 2024-25 & 2025-26</Text>
      <Text style={styles.infoText}>
        Our advanced GST Calculator is a comprehensive tool designed to simplify Goods and Services Tax calculations 
        for businesses, manufacturers, wholesalers, and individual buyers across India. Whether you need to calculate 
        GST inclusive or exclusive amounts, determine CGST and SGST breakdowns, or plan your business pricing strategy, 
        our calculator provides instant, accurate results with detailed explanations.
      </Text>

      <Text style={styles.infoSubtitle}>What is GST (Goods and Services Tax)?</Text>
      <Text style={styles.infoText}>
        Goods and Services Tax (GST) is India's unified indirect tax system that replaced multiple taxes like VAT, 
        Service Tax, and Excise Duty on July 1, 2017. GST follows the principle of "One Nation, One Tax" 
        and is a comprehensive, multi-stage, destination-based tax levied on every value addition in the supply chain. 
        This revolutionary tax system has simplified compliance, eliminated cascading effects, and created a unified 
        national market for goods and services.
      </Text>

      <Text style={styles.infoSubtitle}>Key Benefits of GST System</Text>
      <View style={styles.bulletList}>
        <Text style={styles.bulletPoint}>• <Text style={styles.bulletBold}>Elimination of Cascading Tax:</Text> GST removes "tax on tax" by allowing input tax credit, reducing overall tax burden</Text>
        <Text style={styles.bulletPoint}>• <Text style={styles.bulletBold}>Simplified Tax Structure:</Text> Single tax replacing multiple indirect taxes for easier compliance</Text>
        <Text style={styles.bulletPoint}>• <Text style={styles.bulletBold}>Transparent Pricing:</Text> Clear visibility of tax component in final price for consumers</Text>
        <Text style={styles.bulletPoint}>• <Text style={styles.bulletBold}>Input Tax Credit:</Text> Businesses can claim credit for GST paid on purchases, reducing tax liability</Text>
        <Text style={styles.bulletPoint}>• <Text style={styles.bulletBold}>Digital Compliance:</Text> Online filing and payment system for better transparency and efficiency</Text>
      </View>

      <Text style={styles.infoSubtitle}>GST Rate Structure in India</Text>
      <Text style={styles.infoText}>
        GST operates on a four-tier rate structure designed to keep essential goods affordable while appropriately 
        taxing luxury items:
      </Text>
      <View style={styles.bulletList}>
        <Text style={styles.bulletPoint}>• <Text style={styles.bulletBold}>0% GST:</Text> Essential items like fresh milk, eggs, vegetables, and educational services</Text>
        <Text style={styles.bulletPoint}>• <Text style={styles.bulletBold}>5% GST:</Text> Basic necessities like LPG, sugar, tea, coffee, and branded food items</Text>
        <Text style={styles.bulletPoint}>• <Text style={styles.bulletBold}>12% GST:</Text> Processed foods, medicines, computers, and mobile phones</Text>
        <Text style={styles.bulletPoint}>• <Text style={styles.bulletBold}>18% GST:</Text> Most goods and services including restaurants, IT services, and capital goods</Text>
        <Text style={styles.bulletPoint}>• <Text style={styles.bulletBold}>28% GST:</Text> Luxury items like cars, air conditioners, cigarettes, and aerated drinks</Text>
      </View>

      <Text style={styles.infoSubtitle}>Types of GST</Text>
      <Text style={styles.infoText}>
        GST is categorized into four types based on the nature and location of transactions:
      </Text>
      <View style={styles.bulletList}>
        <Text style={styles.bulletPoint}>• <Text style={styles.bulletBold}>CGST (Central GST):</Text> Collected by Central Government on intra-state transactions</Text>
        <Text style={styles.bulletPoint}>• <Text style={styles.bulletBold}>SGST (State GST):</Text> Collected by State Government on intra-state transactions</Text>
        <Text style={styles.bulletPoint}>• <Text style={styles.bulletBold}>UTGST (Union Territory GST):</Text> Collected by UT Government in union territories</Text>
        <Text style={styles.bulletPoint}>• <Text style={styles.bulletBold}>IGST (Integrated GST):</Text> Collected by Central Government on inter-state and import transactions</Text>
      </View>

      <Text style={styles.infoSubtitle}>GST Calculation Formulas</Text>
      <Text style={styles.infoText}>Understanding GST calculation is crucial for accurate pricing and compliance:</Text>
      
      <Text style={styles.infoText}><Text style={styles.bulletBold}>For GST Exclusive Pricing:</Text></Text>
      <View style={styles.formulaBox}>
        <Text style={styles.formulaText}>GST Amount = (Original Price × GST Rate) ÷ 100</Text>
        <Text style={styles.formulaText}>Final Price = Original Price + GST Amount</Text>
      </View>

      <Text style={styles.infoText}><Text style={styles.bulletBold}>For GST Inclusive Pricing:</Text></Text>
      <View style={styles.formulaBox}>
        <Text style={styles.formulaText}>GST Amount = Total Price × [GST Rate ÷ (100 + GST Rate)]</Text>
        <Text style={styles.formulaText}>Original Price = Total Price - GST Amount</Text>
      </View>

      <Text style={styles.infoText}><Text style={styles.bulletBold}>CGST and SGST Calculation:</Text></Text>
      <View style={styles.formulaBox}>
        <Text style={styles.formulaText}>CGST = GST Amount ÷ 2</Text>
        <Text style={styles.formulaText}>SGST = GST Amount ÷ 2</Text>
      </View>

      <Text style={styles.infoSubtitle}>How to Use Our GST Calculator</Text>
      <View style={styles.stepsList}>
        <Text style={styles.stepText}><Text style={styles.bulletBold}>Step 1:</Text> Select your user type - Buyer (for purchase calculations), Manufacturer, or Wholesaler (for selling price calculations)</Text>
        <Text style={styles.stepText}><Text style={styles.bulletBold}>Step 2:</Text> Enter the base amount - cost price for manufacturers/wholesalers or purchase price for buyers</Text>
        <Text style={styles.stepText}><Text style={styles.bulletBold}>Step 3:</Text> Set profit margin (for manufacturers/wholesalers) using the interactive slider</Text>
        <Text style={styles.stepText}><Text style={styles.bulletBold}>Step 4:</Text> Choose the applicable GST rate from 0% to 28% based on your product/service category</Text>
        <Text style={styles.stepText}><Text style={styles.bulletBold}>Step 5:</Text> Select whether GST is included in the entered amount or to be added separately</Text>
        <Text style={styles.stepText}><Text style={styles.bulletBold}>Step 6:</Text> View instant results with detailed breakdown of CGST, SGST, and total amounts</Text>
      </View>

      <Text style={styles.infoSubtitle}>GST Registration and Compliance</Text>
      <Text style={styles.infoText}>
        GST registration is mandatory for businesses with annual turnover exceeding ₹40 lakhs (₹20 lakhs for northeastern states). 
        Registered businesses receive a 15-digit GSTIN (GST Identification Number) and must file monthly/quarterly returns. 
        Key compliance requirements include maintaining proper invoices, filing timely returns, and paying GST within due dates.
      </Text>

      <Text style={styles.infoSubtitle}>Input Tax Credit (ITC) Benefits</Text>
      <Text style={styles.infoText}>
        One of GST's major advantages is the Input Tax Credit mechanism, allowing businesses to claim credit for GST 
        paid on purchases against their GST liability on sales. This eliminates the cascading effect of taxes 
        and reduces the overall tax burden on businesses, making products more competitively priced.
      </Text>

      <Text style={styles.infoSubtitle}>Advantages of Using Our GST Calculator</Text>
      <View style={styles.bulletList}>
        <Text style={styles.bulletPoint}>• <Text style={styles.bulletBold}>Instant Accuracy:</Text> Mathematically precise calculations eliminating human errors</Text>
        <Text style={styles.bulletPoint}>• <Text style={styles.bulletBold}>Comprehensive Results:</Text> Detailed breakdown of CGST, SGST, and total amounts</Text>
        <Text style={styles.bulletPoint}>• <Text style={styles.bulletBold}>Multiple User Types:</Text> Tailored calculations for buyers, manufacturers, and wholesalers</Text>
        <Text style={styles.bulletPoint}>• <Text style={styles.bulletBold}>Real-time Updates:</Text> Instant recalculation as you modify inputs</Text>
        <Text style={styles.bulletPoint}>• <Text style={styles.bulletBold}>Visual Representation:</Text> Interactive charts showing tax distribution</Text>
        <Text style={styles.bulletPoint}>• <Text style={styles.bulletBold}>Business Planning:</Text> Profit margin calculations for informed pricing decisions</Text>
        <Text style={styles.bulletPoint}>• <Text style={styles.bulletBold}>Compliance Support:</Text> Accurate calculations ensuring GST compliance</Text>
      </View>

      <View style={styles.tipBox}>
        <Ionicons name="bulb-outline" size={20} color="#4F46E5" />
        <Text style={styles.tipText}>
          <Text style={styles.tipBold}>Pro Tip:</Text> Always verify the applicable GST rate for your specific product or service 
          category before calculations. GST rates can change based on government notifications and product classifications.
        </Text>
      </View>

      <View style={styles.tipBox}>
        <Ionicons name="shield-checkmark-outline" size={20} color="#10B981" />
        <Text style={styles.tipText}>
          <Text style={styles.tipBold}>Compliance Note:</Text> Maintain proper GST invoices and file returns on time to avoid 
          penalties. Use our calculator for accurate pricing but consult a tax professional for complex scenarios.
        </Text>
      </View>

      <View style={styles.tipBox}>
        <Ionicons name="trending-up-outline" size={20} color="#F59E0B" />
        <Text style={styles.tipText}>
          <Text style={styles.tipBold}>Business Strategy:</Text> Use inclusive GST pricing in competitive markets to keep 
          prices attractive, and exclusive GST pricing when you want to clearly show tax transparency to customers.
        </Text>
      </View>
    </View>
   );

  const renderFAQSection = () => (
    <View style={styles.faqContainer}>
      <TouchableOpacity 
        style={styles.faqHeader}
        onPress={() => setShowFAQ(!showFAQ)}
      >
        <Text style={styles.faqTitle}>Frequently Asked Questions (FAQs)</Text>
        <Ionicons 
          name={showFAQ ? "chevron-up" : "chevron-down"} 
          size={24} 
          color={colors.primary} 
        />
      </TouchableOpacity>
      
      {showFAQ && (
        <View style={styles.faqContent}>
          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>1. What is GST and how does it work in India?</Text>
            <Text style={styles.faqAnswer}>
              GST (Goods and Services Tax) is India's unified indirect tax system implemented on July 1, 2017. It replaced multiple taxes like VAT, Service Tax, and Excise Duty. GST is a destination-based tax levied on every value addition in the supply chain, following the principle of "One Nation, One Tax." It eliminates the cascading effect of taxes through the Input Tax Credit mechanism.
            </Text>
          </View>

          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>2. What are the different GST rates applicable in India?</Text>
            <Text style={styles.faqAnswer}>
              India follows a four-tier GST rate structure: 0% (essential items like milk, vegetables), 5% (basic necessities like LPG, sugar), 12% (processed foods, medicines), 18% (most goods and services), and 28% (luxury items like cars, cigarettes). Additionally, some items like petroleum products and alcohol are outside GST purview.
            </Text>
          </View>

          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>3. What is the difference between CGST, SGST, and IGST?</Text>
            <Text style={styles.faqAnswer}>
              CGST (Central GST) and SGST (State GST) are levied on intra-state transactions, with revenue going to Central and State governments respectively. IGST (Integrated GST) is levied on inter-state transactions and imports, with revenue going to the Central government. For intra-state transactions, GST is split equally between CGST and SGST.
            </Text>
          </View>

          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>4. How do I calculate GST on inclusive and exclusive prices?</Text>
            <Text style={styles.faqAnswer}>
              For GST exclusive: GST Amount = (Price × GST Rate) ÷ 100, Final Price = Price + GST Amount. For GST inclusive: GST Amount = Price × [GST Rate ÷ (100 + GST Rate)], Original Price = Price - GST Amount. Our calculator automatically handles both scenarios based on your selection.
            </Text>
          </View>

          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>5. What is Input Tax Credit (ITC) and how does it benefit businesses?</Text>
            <Text style={styles.faqAnswer}>
              Input Tax Credit allows businesses to claim credit for GST paid on purchases against their GST liability on sales. This eliminates the cascading effect of "tax on tax" and reduces the overall tax burden. Businesses can set off input tax credit against output tax liability, paying only the net difference to the government.
            </Text>
          </View>

          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>6. Who needs to register for GST and what is the threshold limit?</Text>
            <Text style={styles.faqAnswer}>
              GST registration is mandatory for businesses with annual turnover exceeding ₹40 lakhs (₹20 lakhs for northeastern states and hill states). E-commerce operators, inter-state suppliers, and certain service providers must register regardless of turnover. Voluntary registration is also allowed for businesses below the threshold.
            </Text>
          </View>

          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>7. How accurate is this GST calculator for business use?</Text>
            <Text style={styles.faqAnswer}>
              Our GST calculator uses precise mathematical formulas as per GST regulations and provides 100% accurate calculations. However, always verify the applicable GST rate for your specific product/service category and consult a tax professional for complex scenarios involving exemptions, reverse charge, or special provisions.
            </Text>
          </View>

          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>8. What are the GST return filing requirements?</Text>
            <Text style={styles.faqAnswer}>
              Regular taxpayers must file GSTR-1 (monthly/quarterly), GSTR-3B (monthly), and annual return GSTR-9. Composition scheme taxpayers file GSTR-4 quarterly. E-commerce operators file GSTR-8 monthly. Returns must be filed by specific due dates, and late filing attracts penalties and interest charges.
            </Text>
          </View>

          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>9. Can I use this calculator for both goods and services?</Text>
            <Text style={styles.faqAnswer}>
              Yes, our GST calculator works for both goods and services. Simply select the appropriate GST rate applicable to your product or service category. The calculation methodology remains the same for both goods and services under the GST regime.
            </Text>
          </View>

          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>10. What is the Composition Scheme under GST?</Text>
            <Text style={styles.faqAnswer}>
              The Composition Scheme is a simplified GST compliance mechanism for small taxpayers with turnover up to ₹1.5 crores. Under this scheme, businesses pay GST at lower rates (1-6% depending on the category) but cannot claim Input Tax Credit or make inter-state supplies. It reduces compliance burden significantly.
            </Text>
          </View>

          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>11. How do I determine the correct GST rate for my product/service?</Text>
            <Text style={styles.faqAnswer}>
              GST rates are determined by HSN (Harmonized System of Nomenclature) codes for goods and SAC (Service Accounting Codes) for services. Check the official GST rate schedule, consult the GST portal, or seek professional advice to identify the correct rate. Misclassification can lead to penalties and interest charges.
            </Text>
          </View>

          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>12. What happens if I charge wrong GST rate to customers?</Text>
            <Text style={styles.faqAnswer}>
              Charging incorrect GST rates can result in penalties, interest charges, and compliance issues. If you charge less than the applicable rate, you must pay the differential amount with interest. If you charge more, you must refund the excess to customers and deposit the correct amount to the government.
            </Text>
          </View>

          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>13. Can I modify GST calculations after generating invoices?</Text>
            <Text style={styles.faqAnswer}>
              Once GST invoices are generated and filed in returns, modifications require specific procedures. You may need to issue credit/debit notes for corrections, file amended returns, or follow the prescribed rectification process. It's crucial to ensure accuracy before finalizing invoices to avoid compliance complications.
            </Text>
          </View>

          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>14. How does GST impact the final price for consumers?</Text>
            <Text style={styles.faqAnswer}>
              GST is ultimately borne by the end consumer. While businesses collect and remit GST to the government, they typically pass on the tax burden to consumers through pricing. However, the elimination of cascading effects under GST often results in lower overall tax burden compared to the previous tax regime.
            </Text>
          </View>

          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>15. What are the penalties for GST non-compliance?</Text>
            <Text style={styles.faqAnswer}>
              GST non-compliance attracts various penalties: late filing of returns (₹50 per day per return), non-payment of tax (18% interest per annum), non-registration (100% of tax amount), and other violations (₹10,000 to ₹25,000). Serious offenses may also attract prosecution and imprisonment.
            </Text>
          </View>
        </View>
      )}
    </View>
  );

  const renderCalculator = () => (
     <View style={styles.calculatorContainer}>
      <View style={styles.formContent}>
        {renderUserTypeSelector()}
        {renderAmountInput()}
        {renderProfitRatioSlider()}
        {renderGSTRateDropdown()}
        {renderGSTInclusionDropdown()}
      </View>
    </View>
  );

  const styles = StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: colors.background,
    },
    header: {
      backgroundColor: '#4F46E5',
      paddingHorizontal: 20,
      paddingVertical: 40,
      paddingTop: isMobile ? 60 : 40,
    },
    headerTitle: {
      fontSize: isMobile ? 24 : 32,
      fontWeight: 'bold',
      color: 'white',
      marginBottom: 12,
      textAlign: isMobile ? 'center' : 'left',
    },
    headerSubtitle: {
      fontSize: 16,
      color: 'rgba(255, 255, 255, 0.9)',
      lineHeight: 24,
      marginBottom: 20,
      textAlign: isMobile ? 'center' : 'left',
    },
    headerTags: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 8,
      justifyContent: isMobile ? 'center' : 'flex-start',
    },
    headerTag: {
      backgroundColor: 'rgba(255, 255, 255, 0.2)',
      paddingHorizontal: 12,
      paddingVertical: 6,
      borderRadius: 16,
    },
    headerTagText: {
      color: 'white',
      fontSize: 12,
      fontWeight: '500',
    },
    hamburgerButton: {
      position: 'absolute',
      top: 20,
      left: 20,
      zIndex: 1,
    },
    calculatorContainer: {
      backgroundColor: colors.card,
      margin: isMobile ? 10 : 20,
      borderRadius: 12,
      padding: 0,
      shadowColor: colors.shadow,
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
    },
    formContent: {
      padding: 20,
    },
    inputGroup: {
      marginBottom: 24,
    },
    label: {
      fontSize: 16,
      fontWeight: '500',
      color: colors.text,
      marginBottom: 8,
    },
    userTypeContainer: {
      flexDirection: 'row',
      gap: 16,
    },
    userTypeButton: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
    },
    radioButton: {
      width: 20,
      height: 20,
      borderRadius: 10,
      borderWidth: 2,
      borderColor: colors.border,
      alignItems: 'center',
      justifyContent: 'center',
    },
    radioButtonSelected: {
      borderColor: '#4F46E5',
    },
    radioButtonInner: {
      width: 10,
      height: 10,
      borderRadius: 5,
      backgroundColor: '#4F46E5',
    },
    userTypeText: {
      fontSize: 16,
      color: colors.text,
    },
    inputContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: 8,
      backgroundColor: colors.background,
    },
    currencySymbol: {
      fontSize: 16,
      color: colors.textSecondary,
      paddingLeft: 12,
    },
    input: {
      flex: 1,
      padding: 12,
      fontSize: 16,
      color: colors.text,
    },
    sliderContainer: {
      marginTop: 8,
    },
    sliderTrack: {
      height: 6,
      backgroundColor: colors.border,
      borderRadius: 3,
      position: 'relative',
      marginHorizontal: 8,
      paddingVertical: 10, // Increase touch area
    },
    sliderTrackActive: {
      backgroundColor: colors.border + '80', // Slightly more transparent when active
    },
    sliderFill: {
      height: 6,
      backgroundColor: '#4F46E5',
      borderRadius: 3,
    },
    sliderThumb: {
      position: 'absolute',
      top: -7,
      width: 20,
      height: 20,
      backgroundColor: '#4F46E5',
      borderRadius: 10,
      marginLeft: -10,
      shadowColor: '#000',
      shadowOffset: {
        width: 0,
        height: 2,
      },
      shadowOpacity: 0.25,
      shadowRadius: 3.84,
      elevation: 5,
    },
    sliderThumbActive: {
      transform: [{ scale: 1.2 }], // Slightly larger when dragging
      shadowOpacity: 0.4,
      elevation: 8,
    },
    sliderLabels: {
      flexDirection: 'row',
      alignItems: 'center',
      marginTop: 8,
    },
    sliderValue: {
      fontSize: 16,
      fontWeight: '600',
      color: colors.text,
    },
    sliderUnit: {
      fontSize: 16,
      color: colors.textSecondary,
      marginLeft: 4,
    },
    dropdownContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: 8,
      padding: 12,
      backgroundColor: colors.background,
    },
    dropdownValue: {
      fontSize: 16,
      color: colors.text,
    },
    gstRateOptions: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 8,
      marginTop: 8,
    },
    gstRateOption: {
      paddingHorizontal: 12,
      paddingVertical: 6,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: 6,
      backgroundColor: colors.background,
    },
    gstRateOptionSelected: {
      borderColor: '#4F46E5',
      backgroundColor: '#4F46E5' + '20',
    },
    gstRateText: {
      fontSize: 14,
      color: colors.text,
    },
    gstRateTextSelected: {
      color: '#4F46E5',
      fontWeight: '600',
    },
    resultsContainer: {
      flex: 1,
      padding: 20,
      backgroundColor: colors.surface,
    },
    emptyResults: {
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: 40,
    },
    emptyResultsText: {
      fontSize: 16,
      color: colors.textSecondary,
      marginTop: 12,
      textAlign: 'center',
    },
    chartContainer: {
      alignItems: 'center',
      marginBottom: 24,
    },
    donutChart: {
      width: 120,
      height: 120,
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: 16,
    },
    donutBase: {
      width: 120,
      height: 120,
      borderRadius: 60,
      position: 'relative',
    },
    donutSegment: {
      position: 'absolute',
      width: 120,
      height: 120,
      borderRadius: 60,
      borderWidth: 20,
    },
    chartLegend: {
      flexDirection: 'row',
      gap: 16,
    },
    legendItem: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
    },
    legendColor: {
      width: 12,
      height: 12,
      borderRadius: 6,
    },
    legendText: {
      fontSize: 14,
      color: colors.text,
    },
    resultsList: {
      gap: 12,
    },
    resultRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingVertical: 8,
    },
    resultLabel: {
      fontSize: 16,
      color: colors.textSecondary,
    },
    resultValue: {
      fontSize: 16,
      fontWeight: '600',
      color: colors.text,
    },
    totalRow: {
      borderTopWidth: 1,
      borderTopColor: colors.border,
      paddingTop: 12,
      marginTop: 8,
    },
    totalLabel: {
      fontSize: 18,
      fontWeight: 'bold',
      color: colors.text,
    },
    totalValue: {
      fontSize: 18,
      fontWeight: 'bold',
      color: '#4F46E5',
    },
    mainContent: {
      flexDirection: (isWeb && !isMobile) ? 'row' : 'column',
      minHeight: (isWeb && !isMobile) ? 600 : 'auto',
    },
    leftPanel: {
      flex: (isWeb && !isMobile) ? 1 : 1,
      backgroundColor: colors.background,
    },
    rightPanel: {
      width: (isWeb && !isMobile) ? 400 : '100%',
      backgroundColor: colors.surface,
      borderLeftWidth: (isWeb && !isMobile) ? 1 : 0,
      borderLeftColor: colors.border,
      padding: 20,
    },
     infoContainer: {
       margin: 20,
       padding: 20,
       backgroundColor: colors.surface,
       borderRadius: 12,
       elevation: 2,
       shadowColor: '#000',
       shadowOffset: { width: 0, height: 2 },
       shadowOpacity: 0.1,
       shadowRadius: 4,
     },
     infoTitle: {
       fontSize: 24,
       fontWeight: 'bold',
       color: colors.text,
       marginBottom: 16,
     },
     infoSubtitle: {
       fontSize: 20,
       fontWeight: '600',
       color: colors.text,
       marginTop: 20,
       marginBottom: 12,
     },
     infoText: {
       fontSize: 16,
       color: colors.textSecondary,
       lineHeight: 24,
       marginBottom: 12,
     },
     bulletList: {
       marginLeft: 16,
       marginBottom: 16,
     },
     bulletPoint: {
       fontSize: 16,
       color: colors.textSecondary,
       lineHeight: 24,
       marginBottom: 4,
     },
     formulaBox: {
       backgroundColor: colors.background,
       padding: 16,
       borderRadius: 8,
       borderLeftWidth: 4,
       borderLeftColor: '#4F46E5',
       marginVertical: 12,
     },
     formulaText: {
       fontSize: 16,
       fontFamily: 'monospace',
       color: colors.text,
       marginBottom: 4,
     },
     stepsList: {
       marginLeft: 8,
       marginBottom: 16,
     },
     stepText: {
       fontSize: 16,
       color: colors.textSecondary,
       lineHeight: 24,
       marginBottom: 8,
     },
     faqContainer: {
       backgroundColor: colors.cardBackground,
       margin: 20,
       borderRadius: 12,
       overflow: 'hidden',
       elevation: 2,
       shadowColor: '#000',
       shadowOffset: { width: 0, height: 2 },
       shadowOpacity: 0.1,
       shadowRadius: 4,
     },
     faqHeader: {
       flexDirection: 'row',
       justifyContent: 'space-between',
       alignItems: 'center',
       padding: 20,
       backgroundColor: colors.primary + '10',
     },
     faqTitle: {
       fontSize: 20,
       fontWeight: 'bold',
       color: colors.text,
     },
     faqContent: {
       padding: 20,
     },
     faqItem: {
       marginBottom: 20,
       paddingBottom: 16,
       borderBottomWidth: 1,
       borderBottomColor: colors.border,
     },
     faqQuestion: {
       fontSize: 16,
       fontWeight: '600',
       color: colors.text,
       marginBottom: 8,
     },
     faqAnswer: {
       fontSize: 15,
       color: colors.textSecondary,
       lineHeight: 22,
     },
   });

  return (
    <ScrollView style={styles.container}>
      {renderHeader()}
      <View style={styles.mainContent}>
        <View style={styles.leftPanel}>
          {renderCalculator()}
        </View>
        <View style={styles.rightPanel}>
          {renderResults()}
        </View>
      </View>
      {renderGSTInformation()}
      {renderFAQSection()}
      <Footer />
    </ScrollView>
  );
};

export default GSTCalculatorScreen;